import { PageHero, LocalBusinessSchema } from '@components/common';
import { CALENDLY_URL } from '@constants';
import { Shield, Target, Users, Clock, MapPin } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function BirminghamPage() {
  return (
    <>
      <LocalBusinessSchema city="Birmingham" latitude={52.4862} longitude={-1.8904} />
      <PageHero
        title="Fractional CISO Services in Birmingham"
        subtitle="Cybersecurity leadership for the Midlands' digital transformation"
        eyebrow="Birmingham"
        seoTitle="Fractional CISO Birmingham | The CISO Network"
        seoDescription="Expert fractional CISO services for Birmingham and Midlands businesses. Access senior cybersecurity leadership without the full-time cost."
      />

      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Cybersecurity for the Midlands Engine
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Birmingham stands at the heart of the UK's manufacturing and professional services
                  sectors, with a rapidly growing digital economy spanning fintech, healthtech, and
                  Industry 4.0. The Greater Birmingham area is home to major corporations,
                  innovative startups, and everything in between.
                </p>
                <p>
                  The West Midlands presents distinct cybersecurity challenges. Manufacturing
                  businesses adopting Industry 4.0 technologies need to secure complex OT/IT
                  converged environments. Professional services firms handle sensitive client data
                  requiring robust protection. Meanwhile, the region's growing healthtech sector
                  must navigate strict NHS Digital and data protection requirements.
                </p>
                <p>
                  Despite being the UK's second-largest city, Birmingham faces a significant
                  cybersecurity skills gap. Full-time CISOs are expensive and hard to find, leaving
                  many businesses under-protected. Fractional CISO services bridge this gap,
                  providing Midlands businesses with executive-level security leadership on a
                  flexible basis.
                </p>
                <p>
                  Birmingham and the West Midlands remain the UK's manufacturing heartland, with
                  significant automotive, logistics, and professional services clusters. These
                  sectors face growing ransomware threats, supply chain pressure, and customer
                  security requirements. A fractional CISO helps Midlands manufacturers bridge the
                  gap between operational technology and IT security without disrupting production.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <MapPin className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Your Account Manager
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Your account manager understands the unique challenges of Midlands businesses,
                      from manufacturing to professional services.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Clock className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Regional Coverage
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Supporting businesses across the West Midlands, from Birmingham city centre to
                      Coventry, Wolverhampton, and the wider region.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Why Midlands Businesses Choose Fractional CISOs
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Access the expertise you need without the recruitment headache
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'OT/IT Security',
                description:
                  'Secure your Industry 4.0 and manufacturing systems from cyber threats.',
              },
              {
                icon: Target,
                title: 'Cost Effective',
                description: 'Save significantly compared to hiring a full-time Birmingham CISO.',
              },
              {
                icon: Users,
                title: 'Local Knowledge',
                description:
                  'CISOs who understand Midlands industries and regulatory requirements.',
              },
              {
                icon: Clock,
                title: 'Flexible Support',
                description: 'Engage the level of support that matches your business needs.',
              },
            ].map((benefit, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <benefit.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {benefit.title}
                </h3>
                <p className="text-muted-foreground text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO Services in Birmingham - FAQs"
        subtitle="Common questions from Birmingham and West Midlands businesses"
        items={[
          {
            id: 'birmingham-1',
            question: 'How does a fractional CISO work with businesses in Birmingham?',
            answer:
              'Our fractional CISOs work with organisations across the UK, including Birmingham. They can attend meetings on-site or work remotely, depending on what suits your business. We match you with a CISO who understands the security challenges facing manufacturing, logistics, and professional services companies.',
          },
          {
            id: 'birmingham-2',
            question: 'What size of business benefits from a fractional CISO in Birmingham?',
            answer:
              'Fractional CISO services are ideal for growing businesses that need strategic security leadership but are not yet ready for a full-time executive hire. This includes Birmingham startups, scale-ups, and mid-sized companies that are winning larger contracts or facing regulatory requirements.',
          },
          {
            id: 'birmingham-3',
            question: 'What industries do your CISOs cover in Birmingham?',
            answer:
              "Our network includes CISOs with experience in manufacturing, logistics, and professional services. We match you with someone who understands your sector's specific regulatory environment and threat landscape, whether you are navigating FCA requirements, NHS Digital standards, or customer security questionnaires.",
          },
          {
            id: 'birmingham-4',
            question: 'What is the first thing a fractional CISO does when they start?',
            answer:
              'Every engagement begins with discovery. Your CISO assesses your current security posture, understands your business priorities, and develops a tailored roadmap. This typically delivers actionable insights within the first 30 days, with quick wins identified early.',
          },
          {
            id: 'birmingham-5',
            question: 'Can a fractional CISO help with board reporting and governance?',
            answer:
              'Yes. A core part of the fractional CISO role is translating technical security risks into business language for boards and leadership teams. Our CISOs build reporting frameworks, present to boards, and establish governance structures that satisfy regulators and investors.',
          },
        ]}
      />

      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Ready to Secure Your Birmingham Business?
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Speak with our team today and discover how a fractional CISO can protect your digital
            transformation.
          </p>
          <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
            Book a Call
          </a>
        </div>
      </section>
    </>
  );
}
